import { OpalApiRequestParams } from '@opallabs/api'
import { readBlock, readPlan, allPlansQueries } from '@opallabs/api/BFF'

type SmartBlockQuery =
  | OpalApiRequestParams<'ReadSmartBlocksBFFV1'>['query']
  | OpalApiRequestParams<'ReadSmartCategorySmartBlocksBFFV1'>['query']

// Support partial matching when query isn't included in calls to invalidate queries, etc
const addQueryIfPresent = (queryKey: string[], query?: SmartBlockQuery) =>
  query ? [...queryKey, query] : queryKey

export const queryKeys = {
  allPlansByWorkspace: (workspaceUuid: string) =>
    allPlansQueries.queryKey({ workspaceUuid }),

  allBlocks: ({ workspaceId }: { workspaceId: string }) =>
    readBlock.queryKey({
      workspaceUuid: workspaceId,
    }),

  block: ({ blockId, workspaceId }: { blockId: string; workspaceId: string }) =>
    readBlock.queryKey({
      workspaceUuid: workspaceId,
      resourceId: blockId,
    }),

  blockAccess: ({
    blockId,
    planId,
    workspaceId,
  }: {
    blockId: string
    planId?: string
    workspaceId: string
  }) =>
    planId != null
      ? ([workspaceId, 'blockAccess', blockId, { planId }] as const)
      : ([workspaceId, 'blockAccess', blockId] as const),

  blocksByWorkspace: ({ workspaceId }: { workspaceId: string }) => [
    workspaceId,
    'blocks',
  ],

  blockDuplication: (blockDuplicationId: string) => [
    'block_duplication',
    blockDuplicationId,
  ],

  plansByWorkspace: ({
    workspaceUuid,
    is_nested,
  }: {
    workspaceUuid: string
    is_nested?: boolean
  }) =>
    typeof is_nested === 'boolean'
      ? [workspaceUuid, 'plans', { is_nested }]
      : [workspaceUuid, 'plans'],

  planById: ({
    workspaceUuid,
    planId,
  }: {
    workspaceUuid: string
    planId?: string
  }) =>
    readPlan.queryKey({
      workspaceUuid,
      resourceId: planId ?? '',
      queryParams: null,
    }),

  viewAccess: ({
    workspaceUuid,
    viewId,
  }: {
    workspaceUuid: string
    viewId: string
  }) => [...queryKeys.planById({ workspaceUuid, planId: viewId }), 'access'],

  smartBlocksByWorkspace: (workspaceUuid: string) => [
    workspaceUuid,
    'smartBlocks',
  ],

  smartBlocksByPlan: ({
    planId,
    workspaceUuid,
  }: {
    planId: string
    workspaceUuid: string
  }) => [...queryKeys.smartBlocksByWorkspace(workspaceUuid), planId],

  smartBlocksById: ({
    planId,
    smartSwimlaneId,
    query,
    workspaceUuid,
  }: {
    planId: string
    smartSwimlaneId: string
    workspaceUuid: string
    query?: SmartBlockQuery
  }) =>
    addQueryIfPresent(
      [
        ...queryKeys.smartBlocksByPlan({ workspaceUuid, planId }),
        smartSwimlaneId,
      ],
      query
    ),

  smartBlocks: ({
    planId,
    smartSwimlaneId,
    workspaceUuid,
    query,
  }: {
    planId: string
    smartSwimlaneId?: string
    workspaceUuid: string
    query?: SmartBlockQuery
  }) =>
    smartSwimlaneId
      ? queryKeys.smartBlocksById({
          smartSwimlaneId,
          workspaceUuid,
          query,
          planId,
        })
      : addQueryIfPresent(
          queryKeys.smartBlocksByPlan({ workspaceUuid, planId }),
          query
        ),
}
